import ScreenHeaderWithInbox, { HEADER_HEIGHT } from '@app/components/Screen/ScreenHeaderWithInbox'
import { CHANNEL_NO_PROFILE_PNG_IMAGE_SRC, ROUTES } from '@app/constants'
import {
  ChannelContent,
  ChannelDetailed,
  ChannelModel,
  Placement,
  useFollowChannelMutation,
  useGetAdvertisementLazyQuery,
  useGetPrivateChannelDataLazyQuery,
  useGetSportsLazyQuery,
  useSuggestedChannelsLazyQuery,
} from '@app/graphql'
import {
  CurrentUserState,
  PrivateChannelState,
  CurrentCountryState,
  guestMode,
} from '@app/storage'
import { Icon, IconColor, IconSize, IconTypes, Loader, Popup } from '@app/ui'
import {  FC, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useSnapshot } from 'valtio'
import { List } from './ChannelList'
import { CtaType, ListElementProps } from './ChannelList/types'
import { ChannelListModalState, closeChannelListModal } from '@app/storage/channelListModal'

interface PrivateChannelListProps {
  onclose?: () => void;
  hasChannel?: any;
  showUserDataInInboxHeader?: boolean

}

interface ChannelListProps {
  channels: Pick<ChannelModel, 'id' | 'logo' | 'channelName'>[];
  title: string;
  loading: boolean;
  showJoined?: boolean;
  onclose?: () => void;
  showUserDataInInboxHeader?: boolean
}

const ChannelList: FC<ChannelListProps> = ({
  channels,
  title,
  loading,
  showJoined=true,
  showUserDataInInboxHeader,
  onclose = () => null,
}) => {
  const [ getPrivateChannelData ] = useGetPrivateChannelDataLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const [ getSports ] = useGetSportsLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const [ getAds ] = useGetAdvertisementLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  })
  const [ followChannel, { loading: loadingFollowChannel } ] =
    useFollowChannelMutation()

  const [ addToChannel, setAddToChannel ] = useState(false)

  const { countryId, isTipster, followedChannels: followed, ownedChannels } = useSnapshot(CurrentUserState)

  const [ adminJoinDisabled, setAdminJoinDisabled ] = useState(false)
  const { id: currentCountryId } = useSnapshot(CurrentCountryState)
  const { opened: channelListState } = useSnapshot(ChannelListModalState)

  const allUserChannels: ChannelDetailed[] = useMemo(() => {
    return Object.values(
      [ ...ownedChannels, ...followed ].reduce((channels, channel) => {
        channels[channel.id] = channel
        return channels
      }, {})
    )
  }, [ ownedChannels, followed ])

  const {
    logo: channelLogo,
    title: channelTitle,
  } = useSnapshot(PrivateChannelState)
  const followedChannelIds = allUserChannels.map(({ id }) => id)


  const navigate = useNavigate()
  const { t } = useTranslation()

  const onSelectChannel = (channel) => {
    setAddToChannel(true)
    if(isTipster && !allUserChannels.includes(channel.id)) {
      setAdminJoinDisabled(true)
      setAddToChannel(false)
      return
    }
    if (!(guestMode() || allUserChannels.includes(channel.id))) {
      followChannel({ variables: { channelId: channel.id } })
        .catch(() => {
          PrivateChannelState.newChannelId = null
        })
    }

    channelFeeds(channel.id).then(() => {
      setAddToChannel(false)
      PrivateChannelState.id = channel.id
      PrivateChannelState.logo = channel?.otherElementData?.logo
      PrivateChannelState.title = channel.title
      localStorage.setItem('currentChannelId', channel.id)
      onclose()
      closeChannelListModal()
      navigate(ROUTES.channel.short, { replace: true, state: { channelId: channel.id } })
    })

  }

  const elements = useMemo(() => {
    if (!channels.length || loading) return channels

    const mappedChannels = channels.map((channel) => {
      const hasJoinedChannel = followedChannelIds.includes(channel.id)
      return {
        id: channel.id,
        title: channel.channelName,
        onSelectElement: (data) => {
          onSelectChannel(data)
        },
        image: channel.logo ?? CHANNEL_NO_PROFILE_PNG_IMAGE_SRC,
        caption: `${t('common.channel')}`,
        ctaTitle: hasJoinedChannel ? t('incognito.channelList.GuestCTATitle') : t('common.join'),
        ctaType: hasJoinedChannel ? CtaType.PRIMARY : CtaType.OUTLINE,
        hasJoinedChannel
      }
    })

    return mappedChannels.sort((a, b) => {
      if (a.hasJoinedChannel && !b.hasJoinedChannel) return -1
      if (!a.hasJoinedChannel && b.hasJoinedChannel) return 1
      return 0
    })
  }, [ loading, followedChannelIds, channels.length  ]) as ListElementProps[]

  const joinedChannelsElements = useMemo(() => {
    return allUserChannels.map((channel) => ({
      id: channel.id,
      title: channel.channelName,
      image: channel.logo ?? CHANNEL_NO_PROFILE_PNG_IMAGE_SRC,
      onSelectElement: (data) => {
        onSelectChannel(data)
      },
      caption: `${t('common.channel')}`,
      selected: followedChannelIds.includes(channel.id),
      ctaTitle: t('incognito.channelList.GuestCTATitle'),
      ctaType: CtaType.PRIMARY,
      otherElementData: channel
    }))
  }, [ loading, followed, ownedChannels ])

  const channelFeeds = async (channelId: string) => {
    window.scrollBy(0, 0)
    try {
      const response = await getPrivateChannelData({
        variables: {
          channelId,
          limit: 10,
        },
      })

      const { privateChannelContent, channelDetailed } = response.data ?? {}
      const { feed, nextCursor } = privateChannelContent ?? {}
      if (feed)
        PrivateChannelState.channelData =
        feed as ChannelContent[]
      PrivateChannelState.offset = nextCursor
      PrivateChannelState.hasMoreData = true
      PrivateChannelState.pinnedContent = channelDetailed?.pinnedContent
      const channel = channels?.find(({ id }) => id === channelId)
      PrivateChannelState.logo = channel?.logo
      PrivateChannelState.title = channel?.channelName
      PrivateChannelState.id = channelId
      if (!followedChannelIds.includes(channelId)) {
        PrivateChannelState.channelsList = [
          channel,
          ...PrivateChannelState.channelsList,
        ] as unknown as ChannelDetailed[]
      }
      const sportData = await getSports()
      const { sports } = sportData.data
      const adsResult = await getAds({
        variables: {
          channelId: channelId,
          placement: Placement.Channel,
          filter: {
            isActive: true,
            channelIds: [ channelId ],
            countryIds: [ currentCountryId || countryId ],
          },
        },
      })
      PrivateChannelState.adsData = adsResult.data?.adsByPlacement ?? []
      PrivateChannelState.sports = sports
    } catch (e) {
      console.error(e)
    }
  }



  const isLoading = loadingFollowChannel || loading || !channels.length

  const openChannelListModal = () => {
    ChannelListModalState.opened = !channelListState
  }

  const isGuest = guestMode()

  return isLoading ? null : (
    <StyledChannelListParent >
      {addToChannel && <Loader showBanner={true} text={t('feed.joiningChannel')} noTransparency withOverlay />}

      <ScreenHeaderWithInbox />

      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '30%' }}>
        {showJoined && joinedChannelsElements.length > 0 &&
      <StyledFormTitle
      >
        {t('tipsterOnboardingServiceSetup.channelCreationLegend').toUpperCase()}
      </StyledFormTitle>
        }

        {showJoined && <List
          elements={joinedChannelsElements}
        />}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70%' }}>

        <StyledFormTitle  >
          {title.toUpperCase()}
        </StyledFormTitle>

        <List
          elements={elements}
        />
      </div>

      <Popup
        open={adminJoinDisabled}
        onClose={() => setAdminJoinDisabled(false)}
        closeButtonText={t('common.gotIt')}
      >
        {t('validation.joinChannelDisabled')}
      </Popup>
      <AllChannelLink onClick={() => navigate(ROUTES.ChannelList.short)}>
        <p className="label">{t('channels.seeAllChannels')}</p>
        <span className="icon">
          <Icon type={IconTypes.angleRight} size={9 as IconSize} color={'#2568B8' as IconColor} />
        </span>
      </AllChannelLink>
    </StyledChannelListParent>
  )
}

const PrivateChannelList: FC<PrivateChannelListProps> = ({
  onclose,
  showUserDataInInboxHeader
}) => {
  const { t } = useTranslation()

  const [
    getRecommendedChannels,
    { loading: loadingRecommendedChannels, data: channels },
  ] = useSuggestedChannelsLazyQuery({ fetchPolicy: 'no-cache' })

  useLayoutEffect(() => {
    getRecommendedChannels()
  }, [])

  const loading = loadingRecommendedChannels

  return (
    <div style={{ backgroundColor: 'white' }}>
      {loading && <Loader withOverlay /> }
      <StyledChannelListContainer>
        <ChannelList
          channels={channels?.recommendedChannels?.channels ?? []}
          loading={loading}
          title={t('incognito.channelList.suggestedTitle')}
          showJoined={!!(channels?.recommendedChannels?.channels ?? []).length}
          onclose={onclose}
          showUserDataInInboxHeader={showUserDataInInboxHeader}
        />
      </StyledChannelListContainer>
    </div>
  )
}

const AllChannelLink = styled.div`
  cursor: pointer;
  position: relative;
  justify-content: center;
  align-items: center;
  gap: 4px;
  width: 100%;
  display: flex;
  text-align: center;
  .label {
    font-size: 12px;
    color: #476BDE;
    font-weight: 500;
  }
`
const StyledChannelListContainer = styled.div``
const StyledChannelListParent = styled.div<{hasPadding?: boolean}>`
  padding-top: ${({ hasPadding = true }) => (hasPadding  ? '50px' : '0px')};
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${HEADER_HEIGHT}px);
`
const StyledFormTitle = styled.div`
  padding: 10px;
  background-color: var(--r-color-white);
  color: var(--font-color-gray-dark-new);
  font-weight: 600;
  font-size: 12px;

  &.top-offset {
    margin-top: 24px;
  }
`
export default PrivateChannelList
